import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { HABIT_TEMPLATES, FREE_HABIT_LIMIT } from '../utils/constants';
import useStore from '../store';
import { metaRepo } from '../services/db/meta';

export default function Onboarding() {
  const navigate = useNavigate();
  const createHabit = useStore((s) => s.createHabit);
  const existingHabitCount = useStore((s) => s.habits?.length ?? 0);
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  // If the sync engine drops habits into local state while the user
  // is still in the wizard (e.g. they signed in on a fresh install
  // and Dashboard's gate raced), bounce them back to the dashboard
  // — they've already onboarded on another device.
  useEffect(() => {
    if (existingHabitCount > 0) navigate('/', { replace: true });
  }, [existingHabitCount, navigate]);

  const categories = [...new Set(HABIT_TEMPLATES.map((t) => t.category))];

  function toggleTemplate(template) {
    setSelected((prev) => {
      const exists = prev.find((s) => s.name === template.name);
      if (exists) return prev.filter((s) => s.name !== template.name);
      if (prev.length >= FREE_HABIT_LIMIT) {
        toast(`Free plan limited to ${FREE_HABIT_LIMIT} habits`);
        return prev;
      }
      return [...prev, template];
    });
  }

  async function handleFinish() {
    if (selected.length === 0) {
      await metaRepo.markOnboarded();
      navigate('/');
      return;
    }

    setLoading(true);
    try {
      for (const template of selected) {
        await createHabit({
          name: template.name,
          icon: template.icon,
          color: template.color,
          schedule: 'daily',
          targetDays: [0, 1, 2, 3, 4, 5, 6],
        });
      }
      await metaRepo.markOnboarded();
      toast.success(`${selected.length} habit${selected.length > 1 ? 's' : ''} created!`);
      navigate('/');
    } catch (err) {
      toast.error('Failed to create habits');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 safe-area-top px-4 py-8 pb-32">
      <div className="max-w-lg mx-auto">
        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= step ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        {step === 0 && (
          <div className="text-center animate-slide-up">
            <div className="text-6xl mb-4">🎯</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Welcome to Streaks
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              Build lasting habits with the power of streaks. Track your progress,
              celebrate milestones, and become the best version of yourself.
            </p>
            <button
              onClick={() => setStep(1)}
              className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-colors"
            >
              Get Started
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="animate-slide-up">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
              Choose your habits
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Select up to {FREE_HABIT_LIMIT} habits to start tracking
              <span className="ml-1 text-sm">({selected.length}/{FREE_HABIT_LIMIT})</span>
            </p>

            {categories.map((cat) => (
              <div key={cat} className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  {cat}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {HABIT_TEMPLATES.filter((t) => t.category === cat).map((template) => {
                    const isSelected = selected.find((s) => s.name === template.name);
                    return (
                      <button
                        key={template.name}
                        type="button"
                        onClick={() => toggleTemplate(template)}
                        aria-pressed={!!isSelected}
                        aria-label={`${template.name}${isSelected ? ' (selected)' : ''}`}
                        className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <span className="text-xl" aria-hidden="true">{template.icon}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {template.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="sticky bottom-4 flex gap-3 mt-6">
              <button
                onClick={() => setStep(0)}
                className="px-4 py-3 text-gray-600 dark:text-gray-400 font-medium rounded-xl border border-gray-300 dark:border-gray-600"
              >
                Back
              </button>
              <button
                onClick={() => setStep(2)}
                className="flex-1 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-colors"
              >
                {selected.length > 0 ? `Continue with ${selected.length}` : 'Skip'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="text-center animate-slide-up">
            <div className="text-6xl mb-4">🚀</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              You're all set!
            </h2>
            {selected.length > 0 ? (
              <>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Your habits are ready to go:
                </p>
                <div className="flex flex-wrap justify-center gap-2 mb-8">
                  {selected.map((h) => (
                    <span
                      key={h.name}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm"
                    >
                      {h.icon} {h.name}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 mb-8">
                You can always add habits from your dashboard.
              </p>
            )}
            <button
              onClick={handleFinish}
              disabled={loading}
              className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
            >
              {loading ? 'Setting up...' : 'Start Tracking'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
